import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Users, 
  Filter, 
  RefreshCw,
  X,
  Search,
  Calendar,
  Timer
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useTheme } from '../../contexts/ThemeContext';
import MerchantSearchSelect from '../ui/merchant-search-select';
import { Switch } from '../ui/switch';
import { notifySidebarCount } from '../../lib/sidebar-counts';
import { formatCurrencySelector, getCurrencySymbol, isSpecificCurrency } from '../../utils/currencyFormatter';

// Helper to format date for API
const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const Customers = () => {
  const { token, selectedPlatform , backendUrl} = useAuth();
  const { currentPlatform } = usePlatform();
  const { theme } = useTheme();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    country: 'all',
    currency: 'all',
    merchant_id: 'all'
  });

  // Default the currency filter to the platform's configured default currency.
  // Re-applies whenever the platform (or its default) changes; user can still
  // switch to "All Currencies" or any other afterwards.
  const platformDefaultCurrency = (selectedPlatform && selectedPlatform !== 'all')
    ? (currentPlatform?.default_currency || 'all')
    : 'all';
  useEffect(() => {
    if (!currentPlatform) return;
    setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformDefaultCurrency]);

  // Money totals: specific currency selected => that currency's sign (code shown
  // in the label); otherwise USD-equivalent.
  const displayCurrency = isSpecificCurrency(filters.currency) ? filters.currency : 'USD';
  const currencySymbol = getCurrencySymbol(displayCurrency);
  const fetchSeqRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [availableFilters, setAvailableFilters] = useState({
    countries: [],
    currencies: [],
    merchants: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0
  });
  
  // Aggregates for summary cards
  const [aggregates, setAggregates] = useState({
    total_spent: 0,
    total_transactions: 0
  });
  // Client-measured load time (ms) of the most recent data fetch
  const [loadTimeMs, setLoadTimeMs] = useState(null);
  
  // Period filter state (same as Transaction History)
  const [periodFilter, setPeriodFilter] = useState({
    period: 'thismonth',
    customMonth: new Date().getMonth() + 1,
    customYear: new Date().getFullYear()
  });

  // Helper function to get date range based on period filter
  const getDateRange = () => {
    const now = new Date();
    
    switch (periodFilter.period) {
      case 'today':
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(todayStart),
          end_date: formatLocalDateTime(todayEnd)
        };
      case 'last7days':
        const sevenDaysStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
        const todayEndLast7 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(sevenDaysStart),
          end_date: formatLocalDateTime(todayEndLast7)
        };
      case 'thismonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisMonthStart),
          end_date: formatLocalDateTime(thisMonthEnd)
        };
      case 'thisyear':
        const thisYearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        const thisYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisYearStart),
          end_date: formatLocalDateTime(thisYearEnd)
        };
      case 'custom':
        const customStart = new Date(periodFilter.customYear, periodFilter.customMonth - 1, 1, 0, 0, 0);
        const customEnd = new Date(periodFilter.customYear, periodFilter.customMonth, 0, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(customStart),
          end_date: formatLocalDateTime(customEnd)
        };
      case 'alltime':
        return { start_date: null, end_date: null };
      default:
        return { start_date: null, end_date: null };
    }
  };

  useEffect(() => {
    if (token && selectedPlatform) {
      fetchAvailableFilters();
    }
  }, [token, selectedPlatform]);

  useEffect(() => {
    if (token && selectedPlatform) {
      fetchCustomers();
    }
  }, [pagination.page, filters, searchQuery, periodFilter, approvedOnly, token, selectedPlatform]);

  const fetchAvailableFilters = async () => {
    if (!token || !selectedPlatform) return;
    
    try {
      const [countriesRes, currenciesRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/filters/countries?platform_id=${selectedPlatform}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/filters/currencies?platform_id=${selectedPlatform}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/filters/merchants?platform_id=${selectedPlatform}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAvailableFilters({
        countries: countriesRes.data.countries,
        currencies: currenciesRes.data.currencies,
        merchants: merchantsRes.data.merchants || []
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchCustomers = async () => {
    if (!token || !selectedPlatform) return;
    // Latest-request-wins guard: prevents a stale "All currencies" response from
    // overwriting the data after the currency filter has switched to the platform default.
    const seq = ++fetchSeqRef.current;
    const t0 = performance.now();
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
        platform_id: selectedPlatform
      });

      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }

      if (filters.currency && filters.currency !== 'all') {
        params.append('currency', filters.currency);
      }

      if (filters.merchant_id && filters.merchant_id !== 'all') {
        params.append('merchant_id', filters.merchant_id);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      // Toggle: approved-only (default) vs all-status totals
      params.append('approved_only', approvedOnly ? 'true' : 'false');
      
      // Add date range from period filter
      const dateRange = getDateRange();
      if (dateRange.start_date) {
        params.append('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        params.append('end_date', dateRange.end_date);
      }

      const response = await axios.get(`${API}/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (seq !== fetchSeqRef.current) return; // superseded by a newer request
      setCustomers(response.data.customers);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
      setAggregates({
        total_spent: response.data.total_spent || 0,
        total_transactions: response.data.total_transactions || 0
      });
      setLoadTimeMs(performance.now() - t0);
      // Note: sidebar totals are sourced from /api/platform-stats (all-time).
      // This page intentionally does NOT emit any sidebar counts so that
      // filter changes here can't overwrite the platform-wide totals.
      setError(null);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      country: 'all',
      currency: 'all',
      merchant_id: 'all'
    });
    setPeriodFilter({
      period: 'thismonth',
      customMonth: new Date().getMonth() + 1,
      customYear: new Date().getFullYear()
    });
  };

  const handleRefresh = () => {
    fetchCustomers();
  };

  if (loading && customers.length === 0) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Users className="w-8 h-8 text-blue-500" />
              Customers
            </h1>
            <p className="text-muted-foreground mt-2">Loading...</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
            <Users className="w-8 h-8 text-blue-500" />
            Customers
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage and view customer information
          </p>
        </div>
        
        {/* Summary Card */}
        <Card className="shrink-0">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              Period Summary
              <span className="ml-1 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" data-testid="approved-only-summary-badge">
                {approvedOnly ? 'Approved' : 'All'}
              </span>
              {loadTimeMs != null && (
                <span
                  className={`ml-2 inline-flex items-center gap-1 text-[10px] font-medium normal-case tracking-normal px-1.5 py-0.5 rounded ${loadTimeMs < 1000 ? 'bg-green-50 text-green-600' : loadTimeMs < 3000 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}
                  title="Time to load this view"
                  data-testid="customers-load-time"
                >
                  <Timer className="h-3 w-3" />
                  {(loadTimeMs / 1000).toFixed(2)}s
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 px-4">
            <div className="flex gap-6">
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Customers</span>
                <span className="text-lg font-semibold">{pagination.total.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Total Spent ({filters.currency && filters.currency !== 'all' ? filters.currency : 'USD'})</span>
                <span className="text-lg font-semibold text-green-600">{currencySymbol}{aggregates.total_spent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Transactions</span>
                <span className="text-lg font-semibold">{aggregates.total_transactions.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-4 flex-wrap gap-4">
              {/* Period Filter - First Position */}
              <div className="flex items-center gap-2">
                <Select value={periodFilter.period} onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, period: value }))}>
                  <SelectTrigger className="w-36">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7days">Last 7 Days</SelectItem>
                    <SelectItem value="thismonth">This Month</SelectItem>
                    <SelectItem value="thisyear">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="alltime">All Time</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Month/Year Selectors */}
                {periodFilter.period === 'custom' && (
                  <>
                    <Select 
                      value={periodFilter.customMonth.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customMonth: parseInt(value) }))}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={periodFilter.customYear.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customYear: parseInt(value) }))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              
              {/* Search Box */}
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Country:</label>
                <Select value={filters.country || "all"} onValueChange={(value) => handleFilterChange('country', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {availableFilters.countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Currency:</label>
                <Select value={filters.currency || "all"} onValueChange={(value) => handleFilterChange('currency', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-32" data-testid="customers-currency-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Currencies</SelectItem>
                    {Array.from(new Set([
                      ...(availableFilters.currencies || []),
                      ...((filters.currency && filters.currency !== 'all') ? [filters.currency] : []),
                    ])).map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {formatCurrencySelector(currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Merchant:</label>
                <MerchantSearchSelect
                  merchants={availableFilters.merchants}
                  value={filters.merchant_id}
                  onChange={(value) => handleFilterChange('merchant_id', value || 'all')}
                  placeholder="All Merchants"
                  className="w-56"
                  data-testid="customer-merchant-filter"
                />
              </div>

              <div className="flex items-center gap-2 pl-2 border-l border-gray-200" title="When ON, totals reflect approved transactions only. When OFF, all statuses (including declined) are included.">
                <Switch
                  id="approved-only-toggle"
                  checked={approvedOnly}
                  onCheckedChange={setApprovedOnly}
                  data-testid="approved-only-toggle"
                />
                <label htmlFor="approved-only-toggle" className="text-sm font-medium cursor-pointer select-none">
                  Approved only
                </label>
              </div>

              {(filters.country && filters.country !== 'all' || filters.currency && filters.currency !== 'all' || filters.merchant_id && filters.merchant_id !== 'all' || searchQuery) && (
                <Button variant="outline" size="sm" onClick={() => { clearFilters(); setSearchQuery(''); }}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
              
              <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead className="text-right">Total Spent ({filters.currency && filters.currency !== 'all' ? filters.currency : 'USD'})</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-muted/50">
                        <TableCell className="font-mono text-sm">
                          {customer.id}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{customer.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{customer.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {customer.country}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-mono">
                            {customer.currency}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {customer.platform_name || 'Unknown Platform'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="text-sm font-medium">
                            {currencySymbol}{(customer.total_spent || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(customer.transaction_count || 0).toLocaleString()} transactions
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                          >
                            {customer.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(customer.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.total_pages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1 || loading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.total_pages || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Customers;